import { integrationName } from "@sameday/phone/integrations";
import type { Role } from "@types";
import { matchPath } from "react-router";
import { COACH_PERMISSIONS, PHONE_SETTINGS_ROLES } from "@/auth/permissions";
import { GLOBAL_AUTHENTICATED_PATHS } from "./globalAuthenticatedRoutes";
import { loginRouteManifest } from "./publicRouteManifest";
import { SUPER_PATHS } from "./superRoutes";

/**
 * The seat-claim page's path, shared between its route entry and the
 * fail-closed role allowlists below so the two can't drift — a mismatch would
 * deny an invitee the one page they were sent to.
 */
const CLAIM_SEAT_ROUTE_PATH = "/phone/claim-seat";

/**
 * Centralized Route Configuration
 *
 * Single source of truth for all application routes, their access requirements,
 * and metadata. Used for:
 * - Route authorization (RBAC)
 * - Route validation (for programmatic navigation)
 * - Finding the best matching route for a given pathname
 *
 * Key features:
 * - Supports dynamic routes with parameters (e.g., /agents/:id)
 * - Longest-path-first matching to avoid parent routes matching before children
 * - Fail-closed authorization (unknown routes are denied by default)
 */

type RouteAccess = "public" | "authenticated" | "company-required";

type RouteConfig = {
  /** Route path pattern (supports React Router syntax like :id, *) */
  path: string;
  /** Access level required */
  access: RouteAccess;
  /** Roles that can access this route. Empty/undefined = all authenticated users */
  roles?: Set<Role>;
  /** Human-readable title for the route */
  title?: string;
  /** Parent route path (for breadcrumb/hierarchy purposes) */
  parent?: string;
};

/**
 * All application routes with their configuration
 *
 * IMPORTANT: Order doesn't matter - findMatchingRoute sorts by specificity
 * Child routes (longer paths) automatically take precedence over parents
 */
export const routes: RouteConfig[] = [
  {
    path: "/preview/angi",
    access: "public",
  },
  {
    path: "/partner-invitation",
    access: "public",
    title: "Partner invitation",
  },
  {
    path: "/partner-reporting",
    access: "authenticated",
    roles: new Set(["partner", "super"]),
    title: "Partner reporting",
  },
  {
    path: "/super/partners",
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Partners",
  },
  // ============ PUBLIC ROUTES ============
  { path: "/", access: "public", title: "Login" },
  {
    path: loginRouteManifest.path,
    access: loginRouteManifest.access,
    title: loginRouteManifest.title,
  },
  { path: "/invitation", access: "public", title: "Invitation" },
  {
    path: "/service-portal",
    access: "public",
    title: "Customer service portal",
  },
  { path: "/reset-password", access: "public", title: "Reset password" },
  // Buyer-facing quote (SD-17731); the share token is the credential.
  { path: "/q/:token", access: "public", title: "Your quote" },
  {
    path: "/q/:token/onboarding",
    access: "public",
    title: "Quote onboarding",
  },
  // HubSpot native-calling provider frames (SD-15309). Public: they carry their
  // own scoped calling identity, not an app session, and are embedded by
  // HubSpot. Registering them here also reserves the `hubspot` company slug.
  { path: "/hubspot/provider", access: "public", title: "HubSpot Calling" },
  {
    path: "/hubspot/provider/authorize",
    access: "public",
    title: "HubSpot Calling sign-in",
  },

  // ============ STAFF-ONLY SUPER CONFIGURATION HUB ============
  {
    path: SUPER_PATHS.root,
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Company selection",
  },
  {
    path: SUPER_PATHS.accounts,
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Accounts & companies",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.accountBilling,
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Account billing",
    parent: SUPER_PATHS.accounts,
  },
  {
    // Sales quotes. Staff tooling that sits beside the account directory — a
    // quote may reference an account but is not scoped to a company.
    path: SUPER_PATHS.quotes,
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Quotes",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.quote,
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Quote",
    parent: SUPER_PATHS.quotes,
  },
  {
    path: SUPER_PATHS.quoteCreateCompany,
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Create company",
    parent: SUPER_PATHS.quote,
  },
  {
    path: SUPER_PATHS.onboardingCalendar,
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Onboarding calendar",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.playbooks,
    access: "company-required",
    roles: new Set(["super"]),
    title: "Playbooks",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.defaultAgents,
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Default agents",
    parent: SUPER_PATHS.root,
  },
  {
    // Global agent-config templates. company-required, not plain
    // authenticated: the editor builds its action list from the scoped
    // company's action catalog.
    path: SUPER_PATHS.agentTemplates,
    access: "company-required",
    roles: new Set(["super"]),
    title: "Default configs",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.agentTemplate,
    access: "company-required",
    roles: new Set(["super"]),
    title: "Default config",
    parent: SUPER_PATHS.agentTemplates,
  },
  {
    path: SUPER_PATHS.companyExpansion,
    access: "company-required",
    roles: new Set(["super"]),
    title: "Expansions",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.companyLegacyVoiceControls,
    access: "company-required",
    roles: new Set(["super"]),
    title: "Legacy main-number controls",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.companyVoiceAiProvider,
    access: "company-required",
    roles: new Set(["super"]),
    title: "Calling setup",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.companySensitiveControls,
    access: "company-required",
    roles: new Set(["super"]),
    title: "Sensitive controls",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.companyInDevelopment,
    access: "company-required",
    roles: new Set(["super"]),
    title: "In development (temporary)",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.companyInternalConnections,
    access: "company-required",
    roles: new Set(["super"]),
    title: "Internal connections",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.insights,
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Insights",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.industryInsights,
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Industry Insights",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.marketingWebsite,
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Marketing website",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.manageSettings,
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Manage settings",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.manageSettingsTab,
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Manage settings",
    parent: SUPER_PATHS.manageSettings,
  },
  {
    path: SUPER_PATHS.reportEmailPreview,
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Report email preview",
    parent: SUPER_PATHS.root,
  },

  {
    path: SUPER_PATHS.monitoring,
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Monitoring",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.managedRoutines,
    access: "authenticated",
    roles: new Set<Role>(["super"]),
    title: "Managed routines",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.portOrders,
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Port orders",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.semanticSearch,
    access: "company-required",
    roles: new Set(["super"]),
    title: "Semantic search",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.demoIdentities,
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Test identities",
    parent: SUPER_PATHS.root,
  },
  {
    path: SUPER_PATHS.dispositionBackfill,
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Data backfills",
    parent: SUPER_PATHS.root,
  },

  // ============ COMPANY SELECTION (authenticated but no company required) ============
  {
    path: GLOBAL_AUTHENTICATED_PATHS.companySelection,
    access: "authenticated",
    roles: new Set(["super", "owner"]),
    title: "Company selection",
  },
  // ============ ADMIN ROUTES (super/owner only) ============
  {
    path: "/playbooks",
    access: "company-required",
    roles: new Set(["super"]),
    title: "Playbooks",
  },
  {
    path: "/playbooks/new",
    access: "company-required",
    roles: new Set(["super"]),
    title: "New playbook",
    parent: "/playbooks",
  },
  {
    path: "/playbooks/:id",
    access: "company-required",
    roles: new Set(["super"]),
    title: "Edit playbook",
    parent: "/playbooks",
  },
  {
    path: "/playbooks/:id/edit",
    access: "company-required",
    roles: new Set(["super"]),
    title: "Edit playbook",
    parent: "/playbooks",
  },
  {
    path: GLOBAL_AUTHENTICATED_PATHS.monitoring,
    access: "authenticated",
    roles: new Set(["super", "admin"]),
    title: "Monitoring",
  },
  {
    path: "/semantic-search",
    access: "company-required",
    roles: new Set(["super", "admin"]),
    title: "Semantic search",
  },
  // ============ PLATFORM ROUTES ============
  // Home bookmarks retain the same chat audience.
  { path: "/signup", access: "public", title: "Sign up" },
  { path: "/onboarding/start", access: "authenticated", title: "Get started" },
  {
    path: "/self-onboarding/join-requests",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Requests to join",
  },
  {
    path: "/self-onboarding",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Get started",
  },
  {
    path: "/super/onboarding",
    access: "authenticated",
    roles: new Set(["super"]),
    title: "Self signup",
  },
  {
    path: "/home",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "member"]),
    title: "Home",
  },
  {
    path: "/notifications",
    access: "company-required",
    title: "Notifications",
  },
  // Company activity feed (SD-10497). Owner/admin/super only, unlike /home.
  {
    path: "/activity",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Activity",
  },
  // "Reporting" in the More menu (SD-9415; merged with the overview dashboard).
  // The landing route includes members for saved AI reports. The performance
  // overview remains restricted to owner/admin/super.
  {
    path: "/reports",
    access: "company-required",
    // Members land on AI reports; the metrics overview keeps its existing gate.
    roles: new Set(["super", "admin", "owner", "member"]),
    title: "Reports",
  },
  {
    path: "/reports/ai/:relicId?",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "member"]),
    title: "AI reports",
    parent: "/reports",
  },
  {
    path: "/reports/:preset",
    access: "company-required",
    // Reopened to owner/admin after metrics validation (SD-10669).
    roles: new Set(["super", "admin", "owner"]),
    title: "Reports",
    parent: "/reports",
  },
  // Legacy report aliases render CompanyNavigate inside the authenticated
  // shell. Register them exactly so the fail-closed guard can reach the
  // redirect while keeping the destination's access policy.
  {
    path: "/reporting",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Reports",
  },
  {
    path: "/stats",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Reports",
  },
  // The retired Operator pages' links (and the historical Company Health
  // Review notifications, which used both health paths) redirect into the
  // Operator chat route. Every role that could open them still reaches the
  // redirect (OPERATOR_READ_ROUTE_PATHS); each is titled for where it leads.
  // A role that cannot open the chat is sent from each retired link to Review.
  {
    path: "/health-audits/:isoWeek",
    access: "company-required",
    title: "Operator chat",
    parent: "/operator",
  },
  {
    path: "/health-reports/:isoWeek",
    access: "company-required",
    title: "Operator chat",
    parent: "/operator",
  },
  {
    path: "/operator",
    access: "company-required",
    title: "Operator chat",
  },
  {
    path: "/operator/recipes",
    access: "company-required",
    title: "Routines",
    parent: "/operator",
  },
  {
    path: "/operator/automations",
    access: "company-required",
    title: "Routines",
    parent: "/operator",
  },
  {
    path: "/operator/automations/:automationId",
    access: "company-required",
    title: "Routines",
    parent: "/operator",
  },
  {
    path: "/operator/tasks/:taskId",
    access: "company-required",
    title: "Operator run",
    parent: "/operator",
  },
  {
    path: "/operator/runs/:runId",
    access: "company-required",
    title: "Routines",
    parent: "/operator",
  },
  // Proposals and findings for the company to decide. Every company member
  // can read; decisions remain role-gated.
  {
    path: "/operator/review",
    access: "company-required",
    title: "Notifications",
    parent: "/operator",
  },
  // The user's private Operator chats. Same audience as the old Home: any company
  // member may chat, and each chat belongs to the person who started it.
  {
    path: "/operator/chat/:chatId?",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "member"]),
    title: "Operator chat",
    parent: "/operator",
  },
  // The company's routines (its scheduled automations), beside the chats in
  // the chat route: the list, or one routine's page on one of its tabs. Its
  // audience is the chat route's: members read it; managers change it.
  {
    path: "/operator/chat/routines/:routineId?/:tab?",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "member"]),
    title: "Routines",
    parent: "/operator",
  },
  {
    path: "/operator/chat/relics/:relicId?",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "member"]),
    title: "AI reports",
    parent: "/operator",
  },
  // The routines' address before they were called routines, which redirects
  // to the new one: the same audience, titled for where it leads.
  {
    path: "/operator/chat/scheduled/:automationId?",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "member"]),
    title: "Routines",
    parent: "/operator",
  },
  // A run's task as the routines view links it, opened as the run's
  // conversation in the chat route. Its audience is the chat route's: members
  // read a run; Operator writers can message it.
  {
    path: "/operator/chat/task/:taskId",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "member"]),
    title: "Operator run",
    parent: "/operator",
  },
  {
    path: "/conversations",
    access: "company-required",
    title: "Conversations",
  },

  // ============ COACH ROUTES ============
  {
    path: "/coach",
    access: "company-required",
    title: "Coach",
  },
  {
    path: "/coach/overview",
    access: "company-required",
    title: "Coach overview",
    parent: "/coach",
  },
  {
    path: "/coach/conversations",
    access: "company-required",
    title: "Coach conversations",
    parent: "/coach",
  },
  {
    path: "/coach/agent-performance",
    access: "company-required",
    title: "Agent performance",
    parent: "/coach",
  },
  {
    path: "/coach/team-performance",
    access: "company-required",
    title: "Team performance",
    parent: "/coach",
  },
  {
    path: "/coach/settings",
    access: "company-required",
    title: "Coach settings",
    parent: "/coach",
  },

  // ============ AGENTS ROUTES ============
  {
    path: "/agents",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Agents",
  },
  {
    path: "/agents/new",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "New agent",
    parent: "/agents",
  },
  {
    path: "/agents/workflows",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Workflows",
    parent: "/agents",
  },
  {
    // Real rendered page (router.tsx `agents/playbooks` -> PlaybooksPage;
    // top-level `/playbooks` redirects here). Declared explicitly so it resolves
    // to itself, not greedily to `/agents/:id` (which would mislabel it
    // "Edit Agent" and resolve access against the wrong route). Static path >
    // `:id` in findMatchingRoute's length sort, so this wins.
    path: "/agents/playbooks",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Playbooks",
    parent: "/agents",
  },
  {
    // Redirects to /settings/transfer-destinations (the page moved out of the
    // Agents workspace); kept so the redirect isn't fail-closed denied before
    // it fires, and declared explicitly so it doesn't resolve greedily to
    // `/agents/:id` and mislabel itself "Edit Agent".
    path: "/agents/transfer-destinations",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "AI transfer destinations",
  },
  {
    path: "/agents/:id",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Edit agent",
    parent: "/agents",
  },
  {
    path: "/agents/:id/greeting",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Agent greeting",
    parent: "/agents/:id",
  },
  {
    path: "/agents/:id/tool-messages",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Agent tool messages",
    parent: "/agents/:id",
  },
  {
    path: "/agents/:id/voices",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Agent voices",
    parent: "/agents/:id",
  },
  {
    path: "/agents/:id/call-logic",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Agent logic",
    parent: "/agents/:id",
  },
  {
    path: "/agents/:id/playbook",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Agent playbook",
    parent: "/agents/:id",
  },
  {
    path: "/agents/:id/advanced-settings",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Agent advanced settings",
    parent: "/agents/:id",
  },

  // ============ CAMPAIGNS ROUTES ============
  {
    path: "/campaigns",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Campaigns",
  },
  {
    path: "/campaigns/new",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "New campaign",
    parent: "/campaigns",
  },
  {
    path: "/campaigns/:id",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Edit campaign",
    parent: "/campaigns",
  },

  // ============ SERVICES ROUTES ============
  // The standalone Services nav retired (SD-10920): /services and
  // /services/pricing-lookup redirect into the Company workspace (router.tsx).
  // The FSM-specific /services/* pages below still resolve for deep links —
  // their nav homes are the integration workspaces (/integrations/*).
  {
    path: "/services",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Services",
  },
  {
    path: "/services/pricing-lookup",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Pricing lookup",
    parent: "/services",
  },
  {
    path: "/services/pricing-catalog",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Pricing catalog",
    parent: "/services",
  },
  {
    path: "/services/job-tags",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Job tags",
    parent: "/services",
  },
  {
    path: "/services/job-types",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Job types",
    parent: "/services",
  },
  {
    path: "/services/call-reasons",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Call reasons",
    parent: "/services",
  },
  {
    path: "/services/campaigns",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Campaign groups",
    parent: "/services",
  },
  {
    path: "/services/custom-fields",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Custom fields",
    parent: "/services",
  },
  {
    path: "/services/membership-types",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Membership types",
    parent: "/services",
  },
  {
    path: "/services/business-units",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Business units",
    parent: "/services",
  },
  {
    path: "/services/zones",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Zones",
    parent: "/services",
  },
  {
    path: "/services/arrival-windows",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Arrival windows",
    parent: "/services",
  },
  {
    path: "/services/technicians",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Technicians",
    parent: "/services",
  },
  {
    path: "/services/availability",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Availability",
    parent: "/services",
  },
  {
    path: "/services/adaptive-capacity",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Adaptive capacity",
    parent: "/services",
  },
  {
    path: "/services/adaptive-capacity/cursor",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Cursor availability",
    parent: "/services/adaptive-capacity",
  },
  {
    path: "/services/adaptive-capacity/cursor/jobs",
    access: "company-required",
    roles: new Set(["super", "owner", "admin"]),
    title: "Cursor seed jobs",
    parent: "/services/adaptive-capacity/cursor",
  },
  {
    path: "/services/adaptive-capacity/cursor/jobs/:jobId",
    access: "company-required",
    roles: new Set(["super", "owner", "admin"]),
    title: "Cursor seed job",
    parent: "/services/adaptive-capacity/cursor",
  },
  {
    path: "/services/adaptive-capacity/rules/new",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "New strategic rule",
    parent: "/services/adaptive-capacity",
  },
  {
    path: "/services/adaptive-capacity/rules/:ruleId",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Edit strategic rule",
    parent: "/services/adaptive-capacity",
  },
  {
    path: "/services/adaptive-capacity/adjustments/new",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "New manual adjustment",
    parent: "/services/adaptive-capacity",
  },
  {
    path: "/services/adaptive-capacity/adjustments/:adjustmentId",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Edit manual adjustment",
    parent: "/services/adaptive-capacity",
  },
  {
    path: "/services/forwarder-rules",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Forwarder rules",
    parent: "/services",
  },
  {
    path: "/services/offices",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Offices",
    parent: "/services",
  },
  {
    path: "/services/price-book-services",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Price Book services",
    parent: "/services",
  },

  // ============ SETTINGS ROUTES (merged Company + Account) ============
  // Old /company/* and /account/* URLs redirect to /settings/* in router.tsx.
  {
    path: "/settings",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Settings",
  },
  {
    path: "/settings/hours",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Company hours",
    parent: "/settings",
  },
  {
    path: "/settings/holidays",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Company holidays",
    parent: "/settings",
  },
  {
    path: "/settings/service-areas",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Service areas",
    parent: "/settings",
  },
  // Priorities was retired, but its entry outlives the page: the URL still
  // resolves (router.tsx redirects it to /settings), and every other retired
  // path keeps its config so `findMatchingRoute` consumers — the post-login
  // `redirectTo` validator, the agent-navigable check, `userHasAccess` —
  // resolve it instead of fail-closing on an unknown route.
  {
    path: "/settings/priorities",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Priorities",
    parent: "/settings",
  },
  {
    path: "/settings/custom-analysis",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Custom analysis",
    parent: "/settings",
  },
  {
    path: "/settings/transfer-destinations",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "AI transfer destinations",
    parent: "/settings",
  },
  // Moved from the retired standalone Services nav (SD-10920); the old
  // /services and /services/pricing-lookup URLs redirect here (router.tsx).
  {
    path: "/settings/custom-services",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Custom services",
    parent: "/settings",
  },
  {
    path: "/settings/pricing-lookup",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Pricing lookup",
    parent: "/settings",
  },
  {
    path: "/settings/services-not-offered",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Services not offered",
    parent: "/settings",
  },
  {
    path: "/settings/faqs",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "FAQs",
    parent: "/settings",
  },
  {
    path: "/settings/reports",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Reports",
    parent: "/settings",
  },
  // Legacy /settings/compliance — redirects to /settings/inbound-consent; kept
  // so the redirect isn't fail-closed denied before it fires.
  {
    path: "/settings/compliance",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Inbound consent",
    parent: "/settings",
  },
  {
    path: "/settings/inbound-consent",
    access: "company-required",
    roles: new Set(["super", "owner"]),
    title: "Inbound consent",
    parent: "/settings",
  },
  {
    path: "/settings/outbound-consent",
    access: "company-required",
    roles: new Set(["super", "owner"]),
    title: "Outbound consent",
    parent: "/settings",
  },
  // Data export + the product-analytics opt-out (former Account -> Advanced,
  // merged here — the old /settings/export and /settings/advanced URLs
  // redirect in router.tsx). Admins open it for the do-not-contact list only;
  // the page keeps its other sections to owners and super.
  {
    path: "/settings/data-privacy",
    access: "company-required",
    roles: new Set(["super", "owner", "admin"]),
    title: "Data & privacy",
    parent: "/settings",
  },
  {
    path: "/settings/export",
    access: "company-required",
    roles: new Set(["super", "owner"]),
    title: "Export",
    parent: "/settings",
  },
  {
    path: "/settings/advanced",
    access: "company-required",
    roles: new Set(["super", "owner"]),
    title: "Advanced",
    parent: "/settings",
  },
  // Former Account section (merged into Settings).
  {
    path: "/settings/team",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Team",
    parent: "/settings",
  },
  {
    path: "/settings/team/invite",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Invite users",
    parent: "/settings/team",
  },
  {
    path: "/settings/companies",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Companies",
    parent: "/settings",
  },
  {
    path: "/settings/billing",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Billing and usage",
    parent: "/settings",
  },
  // Members see and edit only their own preferences here (SD-19135); the
  // page hides everyone else's rows and the company-wide alerts from them.
  {
    path: "/settings/notifications",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "member"]),
    title: "Notifications",
    parent: "/settings",
  },
  {
    path: "/settings/agent-memory",
    access: "company-required",
    roles: new Set(["admin", "owner", "member"]),
    title: "Agent memory",
    parent: "/settings",
  },
  {
    path: "/settings/archived-chats",
    access: "company-required",
    roles: new Set(["admin", "owner", "member"]),
    title: "Archived chats",
    parent: "/settings",
  },
  // Account-level telephony (SD-15879), moved out of the Phone workspace so
  // accounts without the Phone System keep number management and A2P
  // registration.
  {
    path: "/settings/phone-numbers",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Phone numbers",
    parent: "/settings",
  },
  {
    path: "/settings/texting-registration",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Texting registration",
    parent: "/settings",
  },
  // Account-scoped blocked callers (SD-17708). These roles are exactly dale's
  // write set for the endpoint, so the route gate is the only gate the page
  // needs — there is no read-only view to fall back to.
  {
    path: "/settings/blocked-callers",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Blocked callers",
    parent: "/settings",
  },
  {
    path: "/settings/profile",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Profile",
    parent: "/settings",
  },
  {
    path: "/settings/security",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Password",
    parent: "/settings",
  },
  // Legacy /company/* redirect aliases. Keep explicit routeConfig entries so
  // shell authorization and agent navigation can allow known redirects while
  // unknown legacy paths still fail closed.
  {
    path: "/company",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Company",
  },
  {
    path: "/company/hours",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Company hours",
    parent: "/company",
  },
  {
    path: "/company/holidays",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Company holidays",
    parent: "/company",
  },
  {
    path: "/company/service-areas",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Service areas",
    parent: "/company",
  },
  // Retired with the page; kept for the same reason as /settings/priorities.
  {
    path: "/company/priorities",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Priorities",
    parent: "/company",
  },
  {
    path: "/company/custom-analysis",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Custom analysis",
    parent: "/company",
  },
  {
    path: "/company/custom-services",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Custom services",
    parent: "/company",
  },
  {
    path: "/company/pricing-lookup",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Pricing lookup",
    parent: "/company",
  },
  {
    path: "/company/services-not-offered",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Services not offered",
    parent: "/company",
  },
  {
    path: "/company/faqs",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "FAQs",
    parent: "/company",
  },
  {
    path: "/company/compliance",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Compliance",
    parent: "/company",
  },
  {
    path: "/company/export",
    access: "company-required",
    roles: new Set(["super", "owner"]),
    title: "Export",
    parent: "/company",
  },
  // Legacy Phone-workspace redirects (FE-1). These render a <Navigate>
  // through the shell, and unknown routes fail closed (userIsAuthorized), so
  // they keep routeConfig entries.
  {
    path: "/company/transferring-numbers",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "AI transfer destinations",
  },
  {
    path: "/company/text-messaging",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Texting",
  },
  // ============ BOOKING WIZARD + CHAT WIDGET ROUTES ============
  // Top-level, no role restriction (Coach-style): every authenticated
  // company user can reach the page; the expansion-flag teaser handles
  // the disabled state (SD-9083).
  {
    path: "/booking-wizard",
    access: "company-required",
    title: "Online booking",
  },
  {
    path: "/chat-widget",
    access: "company-required",
    title: "Chat Widget",
  },
  // ============ LEGACY ACCOUNT REDIRECTS ============
  // The Account section merged into /settings/* (see SETTINGS ROUTES). Keep
  // explicit routeConfig entries for known redirect aliases so shell
  // authorization and agent navigation do not reject them before redirecting.
  {
    path: "/account",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Account",
  },
  {
    path: "/account/team",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Team",
    parent: "/account",
  },
  {
    path: "/account/team/invite",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Invite users",
    parent: "/account/team",
  },
  {
    path: "/account/companies",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Companies",
    parent: "/account",
  },
  {
    path: "/account/phone-numbers",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Numbers",
  },
  {
    path: "/account/texting",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Texting",
  },
  {
    path: "/account/billing",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Billing and usage",
    parent: "/account",
  },
  {
    path: "/account/advanced",
    access: "company-required",
    roles: new Set(["super", "owner"]),
    title: "Advanced",
    parent: "/account",
  },

  // ============ DISPATCH BOARD (SCHEDULING-DISPATCH-PLAN.md Phase 2) ============
  // Native scheduling for no-FSM companies. Dispatcher and technician routes
  // are additionally fail-closed by the allowlists below.
  {
    path: "/dispatch",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "dispatcher"]),
    title: "Dispatch",
  },
  {
    path: "/dispatch/jobs/:jobId",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "dispatcher", "technician"]),
    title: "Job record",
    parent: "/dispatch",
  },
  {
    path: "/dispatch/roster",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "dispatcher"]),
    title: "Roster",
    parent: "/dispatch",
  },
  {
    path: "/dispatch/tech/:technicianId",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "dispatcher"]),
    title: "Tech day",
    parent: "/dispatch",
  },
  {
    path: "/dispatch/customers",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "dispatcher"]),
    title: "Customers",
    parent: "/dispatch",
  },
  {
    path: "/dispatch/customers/:customerId",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "dispatcher"]),
    title: "Customer profile",
    parent: "/dispatch",
  },
  {
    path: "/dispatch/my-day",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "technician"]),
    title: "My day",
    parent: "/dispatch",
  },

  // ============ PHONE WORKSPACE ROUTES ============
  // FE-1 consolidation. Gating mirrors the pre-consolidation routes (all four
  // moved pages were super/admin/owner). Later phone-system WPs loosen this
  // behind the phoneSystemEnabled flag (seated members get the workspace).
  {
    path: "/phone",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Phone",
  },
  {
    path: "/phone/dashboard",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Home",
    parent: "/phone",
  },
  {
    path: "/phone/settings",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "member"]),
    title: "Phone settings",
    parent: "/phone",
  },
  // Deliberately member-inclusive (SD-11757): the ONE phone page a seated
  // member can open — self-service over /seats/me. Route access is gated on
  // the matched LEAF path, so this doesn't loosen the rest of /phone/*.
  {
    path: "/phone/my-settings",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "member"]),
    title: "My Phone settings",
    parent: "/phone",
  },
  // Member-inclusive for the same reason /phone/my-settings is, and then some:
  // this is where an INVITED person finishes their own seat, so it must be
  // reachable by whatever role they were given on the Team page. Access is
  // gated on the matched LEAF path, so listing them here doesn't loosen the
  // rest of /phone/*.
  //
  // `roles` alone does NOT cover coach_csr, rep, dispatcher, or technician:
  // canAccessRoute short-circuits those to their own fail-closed allowlists
  // before it ever reads this set, so the path is listed in each of them too.
  {
    path: CLAIM_SEAT_ROUTE_PATH,
    access: "company-required",
    roles: new Set([
      "super",
      "admin",
      "owner",
      "member",
      "dispatcher",
      "technician",
      "rep",
      "coach_csr",
    ]),
    title: "Finish your Phone setup",
    parent: "/phone",
  },
  {
    path: "/phone/calls",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "History",
    parent: "/phone",
  },
  {
    path: "/phone/seats",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Seats",
    parent: "/phone",
  },
  {
    path: "/phone/queues",
    access: "company-required",
    // Shared with the FIFO-config helper (`canConfigureFifoQueues`) so the route
    // gate and the in-editor FIFO gate can't drift.
    roles: new Set(PHONE_SETTINGS_ROLES),
    title: "Caller groups",
    parent: "/phone",
  },
  {
    path: "/phone/numbers",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Numbers",
    parent: "/phone",
  },
  {
    path: "/phone/port-orders",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Port orders",
    parent: "/phone",
  },
  {
    // Leaf detail page — same gate as the numbers list it hangs off, and
    // parented there so the back/breadcrumb trail lands on the list.
    path: "/phone/numbers/:phoneNumber/flow",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Call flow",
    parent: "/phone/numbers",
  },
  {
    path: "/phone/compliance",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Compliance",
    parent: "/phone",
  },
  // Texting Registration moved to /settings/texting-registration (SD-15879).
  // Both of these now redirect there; kept here so the redirect isn't
  // fail-closed denied before it fires.
  {
    path: "/phone/texting-registration",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Texting registration",
    parent: "/phone",
  },
  {
    path: "/phone/texting",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Texting registration",
    parent: "/phone",
  },
  // The short-lived Phone → Notifications tab — redirects to
  // /settings/notifications; kept so the redirect isn't fail-closed denied
  // before it fires.
  {
    path: "/phone/notifications",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Notifications",
    parent: "/phone",
  },
  // Legacy Texting page — redirects to /settings/notifications; kept so the
  // redirect isn't fail-closed denied before it fires.
  {
    path: "/phone/text-messaging",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Notifications",
    parent: "/phone",
  },
  {
    path: "/phone/forwarding",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "AI transfer destinations",
  },
  {
    // Redirects to /phone/compliance (Recording merged into Compliance);
    // kept so the redirect isn't fail-closed denied before it fires.
    path: "/phone/recording",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Compliance",
    parent: "/phone",
  },

  // ============ TEAM ROUTES ============
  {
    path: "/team",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Team",
  },
  {
    path: "/team/invite",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Invite users",
    parent: "/team",
  },

  // ============ INTEGRATIONS ROUTES ============
  // Customer-facing: owners/admins self-serve their integrations (the catalog
  // plus every detail page). Pages needing Sameday-side provisioning gate
  // themselves in-page (e.g. ServiceTitan's access-request teaser).
  {
    path: "/integrations",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Integrations",
  },
  {
    path: GLOBAL_AUTHENTICATED_PATHS.integrationSetup,
    access: "authenticated",
    roles: new Set(["owner", "admin"]),
    title: "Integration setup",
    parent: "/integrations",
  },
  {
    path: "/integrations/apps/connected",
    access: "company-required",
    roles: new Set(["super", "owner", "admin"]),
    title: "Connecting an app",
    parent: "/integrations",
  },
  {
    path: "/integrations/servicetitan",
    access: "company-required",
    roles: new Set(["super", "owner", "admin"]),
    title: integrationName("servicetitan"),
    parent: "/integrations",
  },
  {
    path: "/integrations/fieldroutes",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: integrationName("fieldroutes"),
    parent: "/integrations",
  },
  {
    path: "/integrations/housecallpro",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: integrationName("housecallpro"),
    parent: "/integrations",
  },
  {
    path: "/integrations/maidcentral",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: integrationName("maidcentral"),
    parent: "/integrations",
  },
  {
    path: "/integrations/realgreen",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: integrationName("realgreen"),
    parent: "/integrations",
  },
  {
    path: "/integrations/buildops",
    access: "company-required",
    roles: new Set(["super"]),
    title: integrationName("buildops"),
    parent: "/integrations",
  },
  {
    path: "/integrations/servicefusion",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: integrationName("servicefusion"),
    parent: "/integrations",
  },
  {
    path: "/integrations/thumbtack",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: integrationName("thumbtack"),
    parent: "/integrations",
  },
  {
    path: "/integrations/google_calendar",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: integrationName("google_calendar"),
    parent: "/integrations",
  },
  {
    path: "/integrations/outlook_calendar",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: integrationName("outlook_calendar"),
    parent: "/integrations",
  },
  {
    path: "/integrations/google_lsa",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: integrationName("google_lsa"),
    parent: "/integrations",
  },
  {
    path: "/integrations/yelp",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: integrationName("yelp"),
    parent: "/integrations",
  },
  {
    path: "/integrations/leadperfection",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: integrationName("leadperfection"),
    parent: "/integrations",
  },
  {
    path: "/integrations/hubspot",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: integrationName("hubspot"),
    parent: "/integrations",
  },
  {
    path: "/integrations/salesforce",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: integrationName("salesforce"),
    parent: "/integrations",
  },
  {
    path: "/integrations/watsco_oca",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: integrationName("watsco_oca"),
    parent: "/integrations",
  },
  {
    path: "/integrations/quickbooks",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: integrationName("quickbooks"),
    parent: "/integrations",
  },
  {
    path: "/integrations/eight_by_eight",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: integrationName("eight_by_eight"),
    parent: "/integrations",
  },
  {
    path: "/integrations/voice_for_pest",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: integrationName("voice_for_pest"),
    parent: "/integrations",
  },
  {
    path: "/integrations/voice_for_pest_sip",
    access: "company-required",
    roles: new Set(["super"]),
    title: integrationName("voice_for_pest_sip"),
    parent: "/integrations",
  },
  {
    path: "/integrations/customcrm",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: integrationName("customcrm"),
    parent: "/integrations",
  },
  {
    path: "/integrations/social/:platform",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Social integration",
    parent: "/integrations",
  },

  // ============ FIELD SALES ROUTES ============
  // Canvassing module (SD-10989). Managers (super/admin/owner) + the restricted
  // `rep` role. For `rep`, the fail-closed allowlist in `canAccessRoute`
  // governs; this roles set gates everyone else (member is deliberately
  // excluded in v1 — see FIELD-SALES-BUILD-SPEC.md §2.1).
  {
    path: "/field-sales",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "rep"]),
    title: "Field Sales",
  },
  {
    path: "/field-sales/territories/:territoryId",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "rep"]),
    title: "Territory",
    parent: "/field-sales",
  },
  {
    path: "/field-sales/map",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "rep"]),
    title: "Field Sales map",
    parent: "/field-sales",
  },
  {
    path: "/field-sales/leaderboards",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "rep"]),
    title: "Leaderboards",
    parent: "/field-sales",
  },
  {
    path: "/field-sales/competitions",
    access: "company-required",
    roles: new Set(["super", "admin", "owner", "rep"]),
    title: "Competitions",
    parent: "/field-sales",
  },
  {
    path: "/field-sales/settings",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Field Sales settings",
    parent: "/field-sales",
  },
  {
    path: "/field-sales/leaderboards/tv",
    access: "company-required",
    roles: new Set(["super", "admin", "owner"]),
    title: "Leaderboard TV",
    parent: "/field-sales/leaderboards",
  },

  // ============ USER SETTINGS ============
  {
    path: GLOBAL_AUTHENTICATED_PATHS.userSettings,
    access: "authenticated",
    title: "User settings",
  },

  // ============ HELP CENTER ============
  {
    path: GLOBAL_AUTHENTICATED_PATHS.help,
    access: "authenticated",
    title: "Help center",
  },
];

/**
 * Pre-computed sorted routes for efficient matching
 * Sorted by path length descending (longest first) to ensure
 * more specific routes are checked before their parents
 */
const sortedRoutes = [...routes].sort((a, b) => b.path.length - a.path.length);

/**
 * Find the best matching route configuration for a given pathname
 *
 * Uses longest-path-first matching to ensure child routes take precedence
 * over their parents. For example, `/settings/hours` will match before `/settings`.
 *
 * @param pathname - The URL pathname to match
 * @returns The matching RouteConfig or undefined if no match found
 *
 * @example
 * findMatchingRoute('/settings/hours') // Returns company hours route config
 * findMatchingRoute('/agents/123')     // Returns /agents/:id route config
 * findMatchingRoute('/unknown')        // Returns undefined
 */
export function findMatchingRoute(pathname: string): RouteConfig | undefined {
  // Try to find a matching route, checking longest paths first
  for (const route of sortedRoutes) {
    const match = matchPath(route.path, pathname);
    if (match) {
      return route;
    }
  }
  return undefined;
}

/**
 * Whether an in-app agent may route the user to this pathname: the route must
 * exist AND not be public. Public routes
 * (`/login`, `/reset-password`, …) are real routes but are sign-in surfaces —
 * sending an already-authenticated user there mid-conversation is never the
 * intent, so they're excluded. This is existence-only; the caller still applies
 * `userIsAuthorized` for the role/company gate.
 *
 * @example
 * isAgentNavigableRoute('/campaigns')   // true
 * isAgentNavigableRoute('/agents/123')  // true (param routes resolve)
 * isAgentNavigableRoute('/login')       // false (public sign-in surface)
 * isAgentNavigableRoute('/nope')        // false (unknown)
 */
export function isAgentNavigableRoute(pathname: string): boolean {
  const route = findMatchingRoute(pathname);
  return route !== undefined && route.access !== "public";
}

/**
 * Operator reads every company member may open, restricted roles included:
 * notifications, Review, and the retired Operator pages' links, which redirect
 * (the Operator home sends whoever cannot open the chat route to Review).
 */
const OPERATOR_READ_ROUTE_PATHS: ReadonlySet<string> = new Set([
  "/notifications",
  "/operator",
  "/operator/recipes",
  "/operator/automations",
  "/operator/automations/:automationId",
  "/operator/review",
  "/operator/tasks/:taskId",
  "/operator/runs/:runId",
  "/health-audits/:isoWeek",
  "/health-reports/:isoWeek",
]);

/**
 * Permission → route(s) it unlocks for a `coach_csr`.
 *
 * The coach_csr route allowlist is DERIVED from the user's effective permissions
 * rather than hardcoded, so the FE projection tracks the backend grant: each
 * row-scoped coach permission maps to the page it powers. (Mirrors dale's
 * coach_csr grant: `coach.conversation.read` + `coach.dashboard.agent_performance.read`.)
 */
const COACH_CSR_PERMISSION_ROUTES = {
  [COACH_PERMISSIONS.conversationRead]: ["/coach/conversations"],
  [COACH_PERMISSIONS.agentPerformanceRead]: ["/coach/agent-performance"],
} satisfies Record<string, readonly string[]>;

/**
 * Baseline routes a `coach_csr` may always reach, independent of permissions:
 * its own `/user-settings`, and the `/coach` layout route (so its permitted
 * child pages nest/render — the `/coach` index itself redirects coach_csr to an
 * allowed child, see `CoachIndex`).
 */
const COACH_CSR_BASELINE_ROUTES: readonly string[] = [
  "/coach",
  "/user-settings",
  // Personal, self-service, and the only page an invited member exists to
  // visit. The fail-closed roles below short-circuit before a route's own
  // `roles` set is consulted, so listing it there is not enough — see
  // CLAIM_SEAT_ROUTE_PATH.
  CLAIM_SEAT_ROUTE_PATH,
];

/**
 * Build the set of route paths a `coach_csr` may reach from its effective
 * permissions. Fail-closed: a permission the user doesn't hold contributes no
 * routes. This is the single derivation — `canAccessRoute` is the only caller,
 * so the path list isn't duplicated anywhere.
 */
export function coachCsrAllowedRoutePaths(
  permissions: Set<string>,
): Set<string> {
  const allowed = new Set<string>(COACH_CSR_BASELINE_ROUTES);
  for (const [permission, paths] of Object.entries(
    COACH_CSR_PERMISSION_ROUTES,
  )) {
    if (permissions.has(permission)) {
      for (const path of paths) allowed.add(path);
    }
  }
  return allowed;
}

/**
 * Routes a `rep` (Field Sales canvasser, SD-10989) may reach — fail-closed like
 * coach_csr, so the role never inherits the "no roles = all authenticated
 * users" default (which would expose /conversations, /coach, etc.).
 *
 * Static rather than permission-derived: the rep grant is uniform (every rep
 * holds territory/stat reads plus `field_sales.prospect.write`), so
 * there's nothing to derive yet. If per-rep grants ever diverge, switch to the
 * coach_csr permission→routes derivation.
 */
const REP_ALLOWED_ROUTE_PATHS: ReadonlySet<string> = new Set([
  CLAIM_SEAT_ROUTE_PATH,
  "/field-sales",
  "/field-sales/map",
  "/field-sales/territories/:territoryId",
  "/field-sales/leaderboards",
  "/field-sales/competitions",
  "/user-settings",
  "/help",
]);

const DISPATCHER_ALLOWED_ROUTE_PATHS: ReadonlySet<string> = new Set([
  CLAIM_SEAT_ROUTE_PATH,
  "/dispatch",
  "/dispatch/jobs/:jobId",
  "/dispatch/roster",
  "/dispatch/tech/:technicianId",
  "/dispatch/customers",
  "/dispatch/customers/:customerId",
  "/user-settings",
  "/help",
]);

const TECHNICIAN_ALLOWED_ROUTE_PATHS: ReadonlySet<string> = new Set([
  CLAIM_SEAT_ROUTE_PATH,
  "/dispatch/my-day",
  "/dispatch/jobs/:jobId",
  "/user-settings",
  "/help",
]);

/**
 * Check if a user with a given role can access a route
 *
 * @param route - The route configuration to check
 * @param userRole - The user's role (undefined = unauthenticated)
 * @param permissions - The user's effective permission set. Required to resolve
 *   `coach_csr` access (its allowlist is permission-derived); ignored for other
 *   roles. Defaults to empty, which fail-closes coach_csr to baseline routes.
 * @returns true if the user can access the route
 */
export function canAccessRoute(
  route: RouteConfig,
  userRole: Role | undefined,
  permissions: Set<string> = new Set(),
): boolean {
  // Unauthenticated users can only access public routes
  // This prevents edge case where roles: undefined + userRole: undefined = true
  if (userRole === undefined) {
    return route.access === "public";
  }

  if (userRole === "partner") {
    return route.access === "public" || route.path === "/partner-reporting";
  }

  // Operator reads are shared by company members, including restricted roles.
  // The loader still requires access to the company in the URL.
  if (OPERATOR_READ_ROUTE_PATHS.has(route.path)) return true;

  // coach_csr is fail-closed to a permission-derived allowlist regardless of
  // whether the route declares `roles` — it must not inherit the "no roles = all
  // authenticated users" default below.
  if (userRole === "coach_csr") {
    return (
      route.access === "public" ||
      coachCsrAllowedRoutePaths(permissions).has(route.path)
    );
  }

  // `rep` is likewise fail-closed to its static allowlist — it must not inherit
  // the "no roles = all authenticated users" default below.
  if (userRole === "rep") {
    return route.access === "public" || REP_ALLOWED_ROUTE_PATHS.has(route.path);
  }

  if (userRole === "dispatcher") {
    return (
      route.access === "public" ||
      DISPATCHER_ALLOWED_ROUTE_PATHS.has(route.path)
    );
  }

  if (userRole === "technician") {
    return (
      route.access === "public" ||
      TECHNICIAN_ALLOWED_ROUTE_PATHS.has(route.path)
    );
  }

  // No roles restriction = all authenticated users can access
  if (!route.roles || route.roles.size === 0) {
    return true;
  }

  // User must have a role that's in the route's allowed roles
  return route.roles.has(userRole);
}

/**
 * Get route configuration by exact path
 *
 * Unlike findMatchingRoute, this requires an exact match
 * without parameter substitution.
 *
 * @param path - The exact route path to find
 * @returns The RouteConfig or undefined if not found
 */
export function getRouteByPath(path: string): RouteConfig | undefined {
  return routes.find((route) => route.path === path);
}

/**
 * Check if a pathname is a public route
 */
export function isPublicRoute(pathname: string): boolean {
  const route = findMatchingRoute(pathname);
  return route?.access === "public";
}
